import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Seo from "@/components/site/Seo";
import SectionHeading from "@/components/site/SectionHeading";
import ProductCard from "@/components/site/ProductCard";
import { fetchProducts, fetchCollectionProducts } from "@/lib/shopify";
import { beautySalonSchema, breadcrumb } from "@/data/schema";
import { AlertCircle, ShoppingBag, Loader2 } from "lucide-react";

function ShopLoader() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
      <Loader2 size={30} className="animate-spin text-gold" />
      <p className="text-[0.7rem] uppercase tracking-[0.3em] text-muted-ivory">
        Loading the collection
      </p>
    </div>
  );
}

export default function Shop() {
  const location = useLocation();
  const collectionHandle = new URLSearchParams(location.search).get("collection");
  const [products, setProducts] = useState([]);
  const [collectionTitle, setCollectionTitle] = useState("");
  const [status, setStatus] = useState("loading"); // loading | done | error
  const [errMsg, setErrMsg] = useState("");

  const load = React.useCallback(() => {
    setStatus("loading");
    setErrMsg("");
    const timeout = setTimeout(() => {
      setErrMsg("The request took too long to respond.");
      setStatus("error");
    }, 15000);
    const request = collectionHandle
      ? fetchCollectionProducts(collectionHandle, 50).then((res) => {
          setCollectionTitle(res.title || "");
          return res.products;
        })
      : fetchProducts(50).then((items) => {
          setCollectionTitle("");
          return items;
        });
    request
      .then((items) => {
        clearTimeout(timeout);
        setProducts(items);
        setStatus("done");
      })
      .catch((e) => {
        clearTimeout(timeout);
        setErrMsg(e?.message || "Unknown error");
        setStatus("error");
      });
  }, [collectionHandle]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Seo
        title="Shop | Lash Supplies & Beauty Products - Golden Touch Vero Beach"
        description="Shop Golden Touch Lash & Brow's curated collection of professional lash supplies, glue and beauty products. Secure checkout, shipped from Vero Beach, Florida."
        path="/shop"
        jsonLd={[
          beautySalonSchema(),
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/shop" },
          ]),
        ]}
      />

      <section className="bg-onyx pt-28 md:pt-36 pb-20 md:pb-28">
        <h1 className="sr-only">
          Golden Touch Shop - Lash Supplies & Beauty Products in Vero Beach, FL
        </h1>
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-center reveal">
            <p className="eyebrow">
              {collectionHandle ? "Digital Guides & E-Books" : "Curated Essentials"}
            </p>
            <div className="mt-4">
              <SectionHeading dark>
                {collectionHandle ? collectionTitle || "E-Books & Guides" : "The Shop"}
              </SectionHeading>
            </div>
            <p className="mx-auto mt-6 max-w-2xl text-muted-ivory">
              {collectionHandle
                ? "Instant-access digital guides and e-books created to educate, reference and elevate your artistry — delivered straight to your inbox after checkout."
                : "Take a touch of Golden Touch home. Professional-grade lash supplies and beauty essentials, delivered to your door with secure checkout."}
            </p>
          </div>

          <div className="mt-16">
            {status === "loading" && <ShopLoader />}

            {status === "error" && (
              <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
                <AlertCircle size={44} className="text-gold/60" />
                <p className="text-muted-ivory">
                  We could not load the shop right now.
                </p>
                {errMsg && (
                  <p
                    data-testid="shop-error-detail"
                    className="max-w-md break-words rounded border border-gold/20 bg-charcoal px-4 py-2 text-xs text-gold/80"
                  >
                    Details: {errMsg}
                  </p>
                )}
                <button
                  type="button"
                  data-testid="shop-retry-btn"
                  onClick={load}
                  className="btn-gold-outline px-7 py-3 text-[0.7rem] tracking-[0.24em] uppercase"
                >
                  Try Again
                </button>
              </div>
            )}

            {status === "done" && products.length === 0 && (
              <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
                <ShoppingBag size={44} className="text-gold/50" />
                <p className="text-muted-ivory">
                  {collectionHandle
                    ? "Our e-books are being added and will appear here soon. Check back shortly."
                    : "New products are on their way. Check back soon."}
                </p>
              </div>
            )}

            {status === "done" && products.length > 0 && (
              <div
                data-testid="product-grid"
                className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4"
              >
                {products.map((p) => (
                  <div key={p.id} className="animate-fade-up">
                    <ProductCard product={p} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
